import { TIMEZONE } from '../budget/engine'
import type {
  DateKey,
  FoodEntry,
  MacroProgress,
  MacroTargets,
  MacroTotals,
  NutritionSettings,
  NutritionState,
  NutritionSummary,
} from './types'

/**
 * Nutrition maths. PURE — no storage, no clock; "today" is passed in.
 *
 * Calories are never entered directly. They are always derived from the macros, so the
 * numbers cannot contradict each other: 4 kcal per gram of protein and carbohydrate,
 * 9 per gram of fat.
 */

export const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 } as const

export const DEFAULT_SETTINGS: NutritionSettings = {
  calorieTarget: 2500,
  proteinPct: 30,
  carbsPct: 50,
  fatPct: 20,
}

export function emptyNutrition(): NutritionState {
  return { settings: { ...DEFAULT_SETTINGS }, entries: [], saved: [] }
}

/** Same timezone rule as the budget: never the device's, so travelling changes nothing. */
export function dateKey(now: Date = new Date()): DateKey {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

export function caloriesOf(macros: {
  proteinG: number
  carbsG: number
  fatG: number
}): number {
  return (
    macros.proteinG * KCAL_PER_G.protein +
    macros.carbsG * KCAL_PER_G.carbs +
    macros.fatG * KCAL_PER_G.fat
  )
}

/** Gram targets derived from the calorie total and the split. */
export function targetsFor(settings: NutritionSettings): MacroTargets {
  const calories = Math.max(0, settings.calorieTarget)
  return {
    calories,
    proteinG: (calories * settings.proteinPct) / 100 / KCAL_PER_G.protein,
    carbsG: (calories * settings.carbsPct) / 100 / KCAL_PER_G.carbs,
    fatG: (calories * settings.fatPct) / 100 / KCAL_PER_G.fat,
  }
}

export function entriesOn(entries: FoodEntry[], date: DateKey): FoodEntry[] {
  return entries
    .filter((e) => e.date === date)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function totalsFor(entries: FoodEntry[]): MacroTotals {
  const totals = entries.reduce(
    (sum, e) => ({
      proteinG: sum.proteinG + e.proteinG,
      carbsG: sum.carbsG + e.carbsG,
      fatG: sum.fatG + e.fatG,
    }),
    { proteinG: 0, carbsG: 0, fatG: 0 }
  )
  return { ...totals, calories: caloriesOf(totals) }
}

export function summarise(
  state: NutritionState,
  now: Date = new Date()
): NutritionSummary {
  const date = dateKey(now)
  const targets = targetsFor(state.settings)
  const totals = totalsFor(entriesOn(state.entries, date))

  const macro = (
    key: MacroProgress['key'],
    label: string,
    consumedG: number,
    targetG: number
  ): MacroProgress => ({
    key,
    label,
    consumedG,
    targetG,
    remainingG: targetG - consumedG,
    fraction: targetG > 0 ? Math.min(1, consumedG / targetG) : 0,
    over: consumedG > targetG,
  })

  return {
    date,
    targets,
    totals,
    caloriesRemaining: targets.calories - totals.calories,
    calorieFraction: targets.calories > 0 ? Math.min(1, totals.calories / targets.calories) : 0,
    macros: [
      macro('protein', 'Protein', totals.proteinG, targets.proteinG),
      macro('carbs', 'Carbs', totals.carbsG, targets.carbsG),
      macro('fat', 'Fat', totals.fatG, targets.fatG),
    ],
  }
}

/**
 * Accepts both "12.5" and "12,5", since a phone keyboard offers whichever the locale
 * feels like. Blank counts as zero; negatives are rejected rather than silently flipped.
 */
export function parseGrams(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.')
  if (trimmed === '') return 0
  if (!/^\d*\.?\d*$/.test(trimmed)) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value < 0) return null
  return Math.round(value * 10) / 10
}

/** Whole numbers, as the spec asks — nobody eats 187.5 g on purpose. */
export function formatGrams(grams: number): string {
  return `${Math.round(grams)} g`
}

export function formatKcal(calories: number): string {
  return `${Math.round(calories)} kcal`
}

/** Percentages must land on 100, or the gram targets quietly stop adding up. */
export function validateSplit(settings: NutritionSettings): string | null {
  if (settings.calorieTarget <= 0) return 'Set a calorie target above zero'
  const total = settings.proteinPct + settings.carbsPct + settings.fatPct
  if (Math.round(total) !== 100) return `The three percentages add up to ${Math.round(total)}%, not 100%`
  if ([settings.proteinPct, settings.carbsPct, settings.fatPct].some((p) => p < 0)) {
    return 'Percentages cannot be negative'
  }
  return null
}
